import { ApiError, getJson, postJsonWithStatus } from './client';
import type { PpkgStatus } from './ppkg';

// --- Canonical response envelope (FD-081 / FD-082) ----------------------------
// Every manager endpoint wraps its payload in the ADR-007 envelope. Payloads
// live under `data`; errors surface as `error.message` / `error.code`. There is
// no body-level statusCode (HTTP status only) and no top-level message —
// summary lines travel inside `data.message` on success/partial results.
export interface ApiEnvelopeError {
  message?: string;
  code?: string;
}

export interface ApiEnvelope<TData> {
  apiVersion: string;
  requestId: string;
  status: 'success' | 'error';
  timestamp: string;
  data: TData;
  error: ApiEnvelopeError;
}

// Exported for the identity API module (FD-098), which reuses the same
// 409-recovery pattern for set-identity-mappings / remove-identity-mappings.
export function isApiEnvelope<TData>(body: unknown): body is ApiEnvelope<TData> {
  return (
    typeof body === 'object' &&
    body !== null &&
    'apiVersion' in body &&
    'status' in body &&
    'data' in body
  );
}

export type MigrationType = 'D2C' | 'T2T' | string;
export type MigrationStatus =
  | 'Pending'
  | 'InProgress'
  | 'Completed'
  | 'Failed'
  | 'Cancelled'
  | string;

// Migration wave values accepted by POST /v1/migrations. Server-enforced enum — any
// other value returns 400. Source of truth is manager/new-migration/run.ps1. Keep
// this list aligned if the server set changes.
//
// FD-279 (FD-ADR-031 §4) narrowed this from Pilot | Wave 1 | Wave 2 | Wave 3 | Production.
// CREATION ONLY: existing "Wave 1..3" campaigns still exist and are still read, filtered
// and rolled up — which is why `Migration.migrationWave` below is `string`, not this union.
// Never narrow the READ type to MigrationWave; it would make legacy rows untypeable.
export const MIGRATION_WAVES = ['Pilot', 'Production'] as const;
export type MigrationWave = (typeof MIGRATION_WAVES)[number];

export interface Migration {
  migrationId: string;
  migrationWave: string;
  migrationName: string;
  migrationType: MigrationType;
  status: MigrationStatus;
  deviceCount: number;
  createdAt: string;
  updatedAt: string;
  sourceTenantId: string | null;
  destTenantId: string | null;
  // FD-110 (GET /v1/migrations{,/{id}} v1.4/v1.5, additive): the admin-entered
  // domain label captured at creation, T2T-only. Contractually present on every
  // record — `null`/"" when absent, never omitted (stable shape) and `null` on
  // records created before FD-110. The UI prefers these over the raw GUID.
  sourceTenantDomain: string | null;
  destTenantDomain: string | null;
  // FD-179 (G-1, additive): the campaign target Autopilot group tag captured at
  // creation (both migration types). "" when unset / on pre-FD-179 records —
  // never omitted (stable shape). Consumed by the ARR-TAGRESOLVE broker op.
  autopilotGroupTag: string | null;
  // FD-262 (v1.5, additive, FD-ADR-030): migration-group membership. Always
  // present, `null` when ungrouped. A DANGLING id — a group that was deleted —
  // arrives as an id with a `null` NAME rather than both null, because
  // "references a group that no longer exists" is a different operator question
  // from "has no group" and must stay visible.
  migrationGroupId: string | null;
  migrationGroupName: string | null;
  // FD-156 (GET /v1/migrations/{id} v1.6, additive, ADR-023): the wave BPRT
  // lifecycle stamped on the Migrations row by FD-135. All three are `null` when
  // the wave was never activated. `bprtPackagePid` is the auto-created
  // `package_<pid>` Entra user id (identifier only — never a credential).
  bprtStatus: string | null;
  bprtPackagePid: string | null;
  bprtExpiresUtc: string | null;
  // FD-205 — the deployment's readiness mode + policy version, and the wave
  // aggregate. `observe` means verdicts are computed and shown but no plan is ever
  // withheld, which the UI must state plainly: otherwise "nothing is blocked" and
  // "blocking is turned off" look identical.
  readinessEnforcement: ReadinessEnforcement;
  readinessPolicyVersion: string | null;
  readinessSummary: ReadinessSummary;
  // FD-221 — the deployment's Track B ruleset state, read from LOCAL state only
  // (no ATC call on this path). Null on a response from a Manager predating FD-221.
  readinessRuleset?: ReadinessRulesetState | null;
  // FD-246 — per-wave Autopilot tag aggregate. Optional: a Manager predating
  // FD-246 omits it entirely.
  autopilotTagSummary?: AutopilotTagSummary | null;
  // FD-321 (FD-ADR-034) — the brokered provisioning package's non-secret state,
  // stamped on the Migrations row by FD-319 and read by FD-320 at delivery.
  //
  // OPTIONAL, AND THE DISTINCTION BETWEEN THE THREE CASES IS LOAD-BEARING:
  //   * a value                 — the row says this
  //   * `null`                  — projected, and there is no package yet
  //   * ABSENT (`undefined`)    — this Manager does not project package state
  //
  // The third case was live when these were typed and NO LONGER IS: FD-328 added
  // the projection to `ConvertTo-MigrationView`, so a current Manager sends all
  // four. They stay OPTIONAL because a Manager deployed before FD-328 omits them,
  // and the UI must render absence as "not reported" and NEVER as "not built" — a
  // built package showing as absent is the FD-269 class of defect (a field
  // reporting a state it cannot know), and it would tell an operator a deliverable
  // wave is not deliverable.
  ppkgStatus?: PpkgStatus | null;
  ppkgBuiltUtc?: string | null;
  ppkgSizeBytes?: number | null;
  ppkgAckPending?: boolean | null;
  // FD-497 — WHEN the delivery receipt landed, read from the `PpkgAckedUtc` column
  // PpkgLifecycleHelper has stamped since FD-319 and nothing projected until now.
  // Read it WITH `ppkgAckPending`, which is the state; this is only its timestamp.
  //
  // `null` covers two honest states and neither is an error: the package was never
  // built, or it is stored-but-not-acked (`ppkgAckPending === true`, the ack failed
  // and was deliberately not retried by rebuilding). The server projects null rather
  // than the empty string the column actually holds in that second case.
  ppkgAckedUtc?: string | null;
  // FD-345 — the three diagnostic fields FD-328 deliberately omitted, reversed on
  // evidence: diagnosing the 2026-08-21 failures required reading the Migrations
  // table directly because nothing exposed them. All three are non-secret.
  //
  // `ppkgSha256` is the digest ATC asserted and the Manager verified, and it is
  // the value that correlates a delivery against an ATC artifact. It is the SAME
  // digest the device receives on its flight plan (FD-ADR-034 R3).
  //
  // `ppkgPackageId` IS CORRELATION ONLY — do not use it to identify what a device
  // installed. ATC's worker generates a fresh random GUID as the package's
  // internal PackageID, so this value does not appear inside the artifact; it
  // names the build request AirBridge reserved before calling ATC.
  ppkgPackageId?: string | null;
  ppkgBuildId?: string | null;
  ppkgSha256?: string | null;
  // FD-371 (FD-ADR-035) — the first-device canary hold, stamped at wave
  // activation and projected null-stable by `get-migration`. OPTIONAL like the
  // Ppkg* fields above: a Manager deployed before FD-371 omits them entirely,
  // and the console must render that as "not reported", never as "canary off".
  //   canaryMode        — 'on' | 'off' once a wave has been activated; null
  //                       before first activation
  //   canarySerial /    — the device the FD-370 gate selected as the canary;
  //   canaryJobId         null until the first delivery stamps them
  //   canaryReleasedUtc — the audited operator release (FD-371); null while
  //   canaryReleasedBy    the hold stands (or when it resolved on its own)
  canaryMode?: 'on' | 'off' | null;
  canarySerial?: string | null;
  canaryJobId?: string | null;
  canaryReleasedUtc?: string | null;
  canaryReleasedBy?: string | null;
  // FD-434 (FD-ADR-041 decision 8) — the wave's licence ledger: what activation
  // reserved, what the per-device settles burned, what completion released.
  //
  // FOUR DISTINCT STATES, AND COLLAPSING ANY TWO IS THE DEFECT THIS FIELD EXISTS
  // TO PREVENT:
  //   * ABSENT (`undefined`) — this Manager predates FD-434 and projects no
  //                            licence state at all. Claim nothing.
  //   * `null`               — projected, and the wave was NEVER ACTIVATED.
  //                            There is no reservation to report.
  //   * object, null counts  — an ACTIVATED wave whose row predates FD-430. It
  //                            really ran and really consumed licences, under
  //                            the superseded per-campaign unit. "Not tracked".
  //   * object, numeric      — the per-device ledger. A count of `0` is a REAL
  //                            answer and must render as `0`.
  //
  // Never `?? 0` and never a truthiness test anywhere near these counts: the
  // server stamps `''` rather than `0` for an absent count (FD-430) precisely so
  // `null` survives the whole way here, and a console that renders 0 for both
  // reports a wave as unlicensed when it is merely old.
  license?: MigrationLicense | null;
}

/**
 * FD-434 — `data.migration.license`. Six non-secret values; the licence token is
 * an app setting used only to call ATC and appears on no row and in no response.
 */
export interface MigrationLicense {
  /** Which bucket the reservation drew on — `pool` or `consumable`. */
  bucket: string | null;
  /** `device` (FD-ADR-041) or `campaign` (the superseded unit). */
  mode: string | null;
  reservedCount: number | null;
  burnedCount: number | null;
  releasedCount: number | null;
  settledUtc: string | null;
}

export type ReadinessEnforcement = 'observe' | 'enforce';

// Unwrapped `data` payload of GET /v1/migrations. getMigrations strips the
// canonical envelope so callers keep the flat shape they had pre-FD-082.
export interface MigrationsListResponse {
  migrations: Migration[];
  continuationToken: string | null;
}

export interface GetMigrationsParams {
  migrationWave?: string;
  pageSize?: number;
  continuationToken?: string | null;
  // FD-262: a group id, or the literal 'none' for the unfiled set. Sent
  // server-side — filtering a paginated scan client-side would be silently
  // wrong, hiding matches behind a "Load more" that looks like the end.
  //
  // NOTE on 'none': the server post-filters that mode on the page, because
  // Table Storage cannot express "property absent" and pre-FD-258 rows have no
  // column at all. So a `none` page can come back SHORTER than pageSize (or
  // empty) while continuationToken is non-null — keep paging, do not treat a
  // short page as the end.
  migrationGroupId?: string;
}

export async function getMigrations(
  params: GetMigrationsParams = {},
): Promise<MigrationsListResponse> {
  const search = new URLSearchParams();
  if (params.migrationWave) search.set('migrationWave', params.migrationWave);
  if (params.migrationGroupId) search.set('migrationGroupId', params.migrationGroupId);
  if (params.pageSize) search.set('pageSize', String(params.pageSize));
  if (params.continuationToken) search.set('continuationToken', params.continuationToken);

  const qs = search.toString();
  const path = qs ? `/v1/migrations?${qs}` : '/v1/migrations';
  const envelope = await getJson<ApiEnvelope<MigrationsListResponse>>(path);
  return envelope.data;
}

// Device row shape returned by GET /v1/migrations/{id} per FD-041. jobId, hardwareHash,
// lastEventType, and lastEventTimestamp may be null for devices that are assigned but
// pre-execution, and lastEventType/lastEventTimestamp stay null until Steve's R-FD-009
// WriteTable extension lands.
//
// FD-102 (v1.4, additive): four manifest user-identity fields sourced from
// DeviceMigrations columns stamped by api/WriteTable from submitManifest
// events. Contractually `null` when absent, never omitted — stable shape for
// the UI. `primaryUserSid` is also the join key into the identity-suggestions
// rows (FD-103: normalized SID).
export interface MigrationDevice {
  serialNumber: string;
  status: string;
  jobId: string | null;
  hardwareHash: string | null;
  lastEventType: string | null;
  lastEventTimestamp: string | null;
  // FD-497 — the device's CURRENT STEP, from the `LastActionId` / `LastActionStatus`
  // columns FD-496 stamps in api/WriteTable's Step-4 merge.
  //
  // READ THESE, NOT `lastEventType`, TO ANSWER "what step is this device on?".
  // `lastEventType` above reads the literal string `"progress"` for every in-flight
  // device in the fleet — api/preFlight-progress sets that subtype on every report —
  // so it cannot distinguish one action from another.
  //
  // `lastActionStatus` is the DEVICE's own word (`started` | `succeeded` | `failed` |
  // `skipped`), not the envelope's mapped `EventStatus`, which collapses
  // started-vs-succeeded into `pending`/`success`.
  //
  // OPTIONAL: additive read fields; a Manager deployed before FD-497 omits them.
  // `null` means the device has never reported progress — which is most devices, most
  // of the time. Render that as an honest empty state, never as a guessed phase.
  lastActionId?: string | null;
  lastActionStatus?: string | null;
  primaryUserSid: string | null;
  primaryUserName: string | null;
  primaryUserSamName: string | null;
  primaryUserDisplayName: string | null;
  // FD-345 — the terminal outcome and its time, stamped by api/WriteTable on the
  // finalizer.report event. Until FD-345 the projection carried NEITHER, so a
  // device read `salvaged` with no recorded outcome and no time.
  //
  // READ BOTH `status` AND `terminalOutcome`; NEITHER IS DERIVABLE FROM THE OTHER.
  // They are separate server columns precisely so a concurrent merge that
  // regresses `Status` under a surviving `TerminalOutcome` is detectable — that is
  // what FD-362's heal-on-read keys off, after two live rows sat at
  // `status: in-progress` under `terminalOutcome: completed`, invisible to wave
  // evaluation. A UI that shows only one of them hides that condition.
  //
  // OPTIONAL: additive read fields; a Manager deployed before FD-345 omits them.
  // `null` means the device has not gone terminal.
  terminalOutcome?: string | null;
  terminalUtc?: string | null;
  // FD-205 / FD-ADR-027 — the server-side preflight readiness verdict, stamped by
  // api/WriteTable on the submitManifest event. Contractually `null` when absent
  // (no manifest submitted yet), never omitted; the two reason arrays are always
  // arrays, never null.
  readinessStatus: ReadinessStatus | null;
  readinessBlockingReasons: ReadinessReason[];
  readinessWarnings: ReadinessReason[];
  readinessEvaluatedUtc: string | null;
  readinessPolicyVersion: string | null;
  readinessOverride: ReadinessOverride | null;
  // FD-221 / FD-ADR-028 R10 — which policy produced this verdict. `readinessSource`
  // is DERIVED SERVER-SIDE, deliberately: the grid, the summary panel and any future
  // reader must not each re-infer "empty version means floor" and disagree.
  //   'ruleset' — ATC's evaluationRuleset (Track B)
  //   'floor'   — the FD-ADR-027 baseline policy
  //   null      — nothing evaluated yet (no manifest)
  // OPTIONAL rather than required-and-nullable like the FD-205 fields above: these
  // are ADDITIVE read fields, and a Manager deployed before FD-221 omits them
  // entirely. Typing them as required would be a lie about what can arrive on the
  // wire, and every reader has to handle absence anyway.
  readinessRulesetVersion?: string | null;
  readinessSource?: ReadinessVerdictSource | null;
  // FD-357 / FD-ADR-036 §4 — whether this device would pass the delivery-time
  // identity gate (409 IDENTITY_NOT_READY). Three DISTINCT states (the FD-246
  // discipline — do not collapse them):
  //   'ready'     — a mapping row matches the device's user and carries a valid
  //                 destUserId; the gate delivers
  //   'not-ready' — no matching row, or the row's destUserId is blank/non-GUID;
  //                 the gate holds. Release = the admin adds the mapping (allowed
  //                 while the wave runs, FD-ADR-036 §3)
  //   'n/a'       — the device has no PrimaryUserSid yet (pre-manifest) — an
  //                 ordinary state, never a warning colour
  // OPTIONAL: additive read field; a Manager deployed before FD-357 omits it.
  identityStatus?: 'ready' | 'not-ready' | 'n/a' | null;
  // FD-246 / OQ-011 — the Autopilot group-tag resolution stamped by the reactive
  // broker (FD-236). Four values the UI must NOT collapse into one another, plus
  // null as a distinct fifth state:
  //   'source'   — a source tag was found in Graph and passed through VERBATIM
  //   'campaign' — no source tag, so the campaign default applied
  //   'none'     — neither; nothing to write
  //   'unknown'  — the Graph read FAILED, so the value is a fallback to the
  //                device-reported tag, not a verdict. This one is a FAULT.
  //   null       — the broker has not run for this device yet (or it predates
  //                FD-236). "Not yet" is not "none".
  // OPTIONAL for the same reason as the FD-221 fields above: a Manager deployed
  // before FD-246 omits them, and typing them required would be a lie about the wire.
  autopilotTagSource?: AutopilotTagSource | null;
  autopilotSourceGroupTag?: string | null;
  autopilotResolvedGroupTag?: string | null;
}

export type ReadinessVerdictSource = 'ruleset' | 'floor';

export type AutopilotTagSource = 'source' | 'campaign' | 'none' | 'unknown';

/**
 * FD-246 — the per-wave Autopilot tag aggregate. `sourceTags` is the input
 * R-FD-032 is designed from: the deferred source→destination tag mapping is gated
 * on real engagement data, so this answers "do devices actually arrive tagged, and
 * with what" from evidence rather than speculation.
 */
export interface AutopilotTagSummary {
  bySource: {
    source: number;
    campaign: number;
    none: number;
    unknown: number;
    notResolved: number;
  };
  sourceTags: { tag: string; count: number }[];
  distinctTagCount: number;
  unknownCount: number;
}

// The verdict vocabulary, mirroring shared/ReadinessPolicyHelper.ps1's
// Get-ReadinessStatuses. `blocked` = a check definitely failed; `unknown` = a
// blocking check could not be evaluated. Both hold under `enforce`, but they mean
// different things to an operator, so they render differently.
export type ReadinessStatus = 'pass' | 'warn' | 'unknown' | 'blocked';

// FD-221 — the deployment's readiness-ruleset state.
//
// This exists because FD-ADR-028's R4 fail-open posture lets AirBridge keep
// working INDEFINITELY on a stale ruleset or on the FD-ADR-027 baseline floor. That
// is the right availability trade, but only defensible if an operator can see which
// policy is actually in force — the observe→enforce decision is made by reading
// exactly this, and flipping while unknowingly stale enforces yesterday's rules.
//
// `floor` is the one field to read first: when true, the baseline policy is what
// evaluates, whatever else is populated. Note it is ALSO true when a pin cannot be
// satisfied by the cached copy — in that state the floor really is what runs, so a
// pinned deployment must never look current.
export interface ReadinessRulesetState {
  configured: boolean;
  missingSettings: string[];
  floor: boolean;
  cached: boolean;
  rulesetVersion: string | null;
  ring: string | null;
  cachedUtc: string | null;
  pinnedVersion: string | null;
  pinSatisfied: boolean | null;
}

// `check` is a stable machine code (FREE_DISK, READINESS_FACTS_MISSING, …).
export interface ReadinessReason {
  check: string;
  detail: string | null;
}

export interface ReadinessOverride {
  by: string;
  utc: string;
  reason: string;
}

// FD-205 — per-migration readiness aggregate, computed server-side from the same
// partition read that produces the device rows. This is what makes the
// observe→enforce decision reviewable at wave scale; nobody reads 2000 grid rows.
export interface ReadinessSummary {
  byStatus: {
    pass: number;
    warn: number;
    unknown: number;
    blocked: number;
    // No manifest submitted yet. Counted apart from `unknown` because the two need
    // different operator actions.
    notEvaluated: number;
  };
  byBlockingReason: Record<string, number>;
  // Devices that WOULD be held under `enforce`, before overrides. The number to
  // look at before flipping the switch.
  wouldBlockCount: number;
  overriddenCount: number;
  // What is ACTUALLY withheld right now — always 0 under `observe`.
  heldCount: number;
}

/**
 * FD-442 (FD-ADR-042 §7) — deployment-wide activation defaults, served so the
 * console does not carry its own copy of them.
 *
 * A SIBLING OF `migration`, NOT A FIELD ON IT, and the server projects it that way
 * deliberately: the value is identical for every migration in the deployment, and
 * hanging it off the row would invite a reader to think this campaign chose it.
 *
 * Optional because a Manager older than FD-442 does not send it. The one consumer
 * falls back to 30 — which is what BOTH sides hard-coded before this existed, so a
 * console ahead of its backend behaves exactly as it did.
 */
export interface WaveDefaults {
  /** `BPRT_DEFAULT_EXPIRY_DAYS`, resolved server-side. Always 1–90. */
  bprtExpiryDays: number;
}

// Unwrapped `data` payload of GET /v1/migrations/{id}.
export interface MigrationDetailResponse {
  migration: Migration;
  devices: MigrationDevice[];
  waveDefaults?: WaveDefaults;
}

export async function getMigrationById(migrationId: string): Promise<MigrationDetailResponse> {
  const envelope = await getJson<ApiEnvelope<MigrationDetailResponse>>(
    `/v1/migrations/${encodeURIComponent(migrationId)}`,
  );
  return envelope.data;
}

// --- GET /v1/migrations/{id}/events?jobId=... (FD-042) -----------------------
// Payload lives at data.{jobId, events} inside the canonical envelope. Each
// event row is projected from the FlightDeckEvents table into the camelCase
// shape produced by New-FlightDeckEvent — standard fields plus any additional
// eventData passthrough keys. The {id} route segment is present for URL
// consistency but the storage query is keyed on jobId alone (partition query on
// FlightDeckEvents).

// EventHelper v1.2 vocabulary, normalized to lowercase by the emitter. Legacy
// boolean callers are converted at event creation ($true → 'success',
// $false → 'error'), so new rows are always one of these strings.
export type FlightDeckEventStatus =
  | 'success'
  | 'error'
  | 'warning'
  | 'pending'
  | 'pass'
  | 'fail'
  | string;

export interface FlightDeckEvent {
  jobId: string;
  rowKey: string;
  eventId: number | string;
  eventSubType: string;
  eventStatus: FlightDeckEventStatus;
  serialNumber: string;
  timeStamp: string;
  processedAt: string;
  // eventData passthrough — any additional fields stamped by the original
  // event emitter flow through unchanged.
  [key: string]: unknown;
}

// Unwrapped `data` payload of GET /v1/migrations/{id}/events.
export interface MigrationEventsResponse {
  jobId: string;
  events: FlightDeckEvent[];
}

export async function getMigrationEvents(
  migrationId: string,
  jobId: string,
): Promise<MigrationEventsResponse> {
  const path =
    `/v1/migrations/${encodeURIComponent(migrationId)}/events` +
    `?jobId=${encodeURIComponent(jobId)}`;
  const envelope = await getJson<ApiEnvelope<MigrationEventsResponse>>(path);
  return envelope.data;
}

// --- POST /v1/migrations -----------------------------------------------------
// Request contract per FD-001 / FD-036 / FD-057. Property names are lowercase
// sourceTenantId / destTenantId — matches the GET response shape used by the
// Migration interface. FD-057 hard-flipped the server from the original
// capital-ID read so both directions now use the same casing.
export interface CreateMigrationRequest {
  migrationName: string;
  // FD-279: was `MigrationWave | string`, which is just `string` — the union was
  // decorative and TypeScript could never catch a bad wave. Now that the accepted
  // set is two values, the type is worth enforcing: a caller must pick from
  // MIGRATION_WAVES or fail to compile, rather than fail with a 400 at runtime.
  // This is the REQUEST type only. `Migration.migrationWave` stays `string`,
  // because legacy "Wave 1..3" rows are still read back and must stay typeable.
  migrationWave: MigrationWave;
  // Optional — the server validates the value only when supplied and stores an
  // empty type otherwise. The UI always sends one.
  migrationType?: 'D2C' | 'T2T';
  // Server-required when migrationType === 'T2T'. FD-057 also validates this
  // as a GUID when present.
  // FD-261 (v1.3, additive/optional, FD-ADR-030): file the new migration into an
  // existing group. Absent is the ordinary case and is byte-identical to before.
  // The server 404s on an unknown or malformed id and creates nothing.
  migrationGroupId?: string;
  sourceTenantId?: string;
  // Server-required when migrationType === 'T2T' (FD-057 added the symmetry
  // the UI had been enforcing client-side since FD-050).
  destTenantId?: string;
  // FD-110 (POST /v1/migrations v1.5, additive/optional): passthrough human
  // LABELS (T2T only). new-migration stores them as-is and does NOT resolve them
  // server-side (FD-ADR-016 keeps new-migration call-free) — the UI resolves the
  // domain→GUID via GET /v1/tenants/resolve and submits BOTH the resolved GUID
  // and the domain label. GUID-only callers omit these (default "" server-side).
  sourceTenantDomain?: string;
  destTenantDomain?: string;
  // FD-179 (G-1, additive/optional): the campaign target Autopilot group tag
  // (both migration types). Sent only when non-empty; stored as-is (default "").
  autopilotGroupTag?: string;
}

// `data` payload of the 201 response. FD-082 normalized the key to migrationId
// (lowercase d) so create and read shapes agree. migrationType echoes the
// request or the literal "not set" when the request omitted it.
interface CreateMigrationData {
  migrationId: string;
  migrationName?: string;
  migrationWave?: string;
  migrationType?: string;
}

export interface CreatedMigration {
  migrationId: string;
}

export async function createMigration(
  req: CreateMigrationRequest,
): Promise<CreatedMigration> {
  const { body: envelope } = await postJsonWithStatus<
    ApiEnvelope<Partial<CreateMigrationData>>,
    CreateMigrationRequest
  >('/v1/migrations', req);
  const id = envelope.data?.migrationId;
  if (!id) {
    throw new Error(
      'POST /v1/migrations succeeded but response is missing data.migrationId',
    );
  }
  return { migrationId: id };
}

/**
 * FD-594 — the machine code `POST /v1/migrations` refuses a Hybrid campaign with
 * when the environment is multi-tenant (Rubix-hosted) and no `destTenantId` was
 * sent. On such an environment the deployment's own tenant is Rubix's, so there
 * is no safe default to fall back to.
 */
export const DESTINATION_TENANT_REQUIRED = 'DESTINATION_TENANT_REQUIRED';

/**
 * The create endpoint's machine code, read from the envelope's `error.code` and
 * nowhere else. There is deliberately NO status fallback: a 400 is not assumed to
 * be `DESTINATION_TENANT_REQUIRED`, because the copy for that code tells the
 * operator exactly what to change, and saying it about some other 400 would send
 * them to fix the wrong thing.
 */
export function createMigrationErrorCode(err: unknown): string | null {
  if (!(err instanceof ApiError)) return null;
  if (!isApiEnvelope<unknown>(err.body)) return null;
  const code = err.body.error?.code;
  return typeof code === 'string' && code.length > 0 ? code : null;
}

// --- POST /v1/migrations/{id}/devices (FD-009) -------------------------------
// Request body per manager/add-devices/run.ps1: { devices: [...] } where each
// entry is either a plain string serial number or an object { serialNumber, ... }.
// Frontend sends plain strings.
//
// Status codes: 200 all added, 207 partial (some added, some rejected /
// indexFailures / mintFailures), 409 all rejected (added.Count === 0 but
// rejected.Count > 0), 404 migration not found.
//
// The 207 path is response.ok (postJsonWithStatus returns normally). The 409
// path throws ApiError but the envelope body still carries per-device detail
// that callers want to render — addDevices unwraps that and returns the data
// either way, with the HTTP status preserved so callers can distinguish
// all-succeeded / partial / all-rejected without re-parsing.
export interface RejectedDevice {
  serialNumber: string;
  reason: string;
}

export interface AddDevicesResponseData {
  migrationId: string;
  migrationName?: string;
  addedCount: number;
  rejectedCount: number;
  added: string[];
  // Devices whose ATC MINT registration failed after the customer-side writes
  // (FD-ADR-016). The DeviceIndex entry is rolled back, so these devices stay
  // assigned to the migration but cannot boot until re-registered.
  mintFailures: RejectedDevice[];
  rejected?: RejectedDevice[];
  indexFailures?: RejectedDevice[];
  indexFailureCount?: number;
  // Summary line ("2 device(s) added, 1 rejected"). Present on success/partial
  // results; on full rejection it moves to the envelope's error.message.
  message?: string;
}

export interface AddDevicesResult {
  status: number;
  message: string | null;
  data: AddDevicesResponseData;
}

export async function addDevices(
  migrationId: string,
  serialNumbers: string[],
): Promise<AddDevicesResult> {
  const path = `/v1/migrations/${encodeURIComponent(migrationId)}/devices`;
  try {
    const { status, body: envelope } = await postJsonWithStatus<
      ApiEnvelope<AddDevicesResponseData>,
      { devices: string[] }
    >(path, { devices: serialNumbers });
    return {
      status,
      message: envelope.data.message ?? null,
      data: envelope.data,
    };
  } catch (err) {
    // 409 all-rejected still has useful per-device detail in the response body.
    // Surface it as a result with the real status so the dialog can render the
    // rejection table instead of a generic error message.
    if (
      err instanceof ApiError &&
      err.status === 409 &&
      isApiEnvelope<AddDevicesResponseData>(err.body) &&
      err.body.data
    ) {
      return {
        status: err.status,
        message: err.body.error.message ?? null,
        data: err.body.data,
      };
    }
    throw err;
  }
}

// --- POST /v1/migrations/{id}/devices/remove (FD-010) ------------------------
// Same request body shape as add-devices: { devices: [...] }. Response data shape
// differs — `removed` + `notFound` arrays instead of `added` + `rejected`.
//
// Status codes: 200 all removed, 207 partial (some removed, some notFound /
// indexFailures / mintFailures), 404 none removed (all were absent). The 404
// path throws ApiError but like add's 409 path, the body carries useful detail.
export interface RemoveDevicesResponseData {
  migrationId: string;
  removedCount: number;
  notFoundCount: number;
  removed: string[];
  // Devices whose ATC MINT deregistration failed (FD-ADR-016). The customer-side
  // removal still succeeded — a stale MINT row self-heals on the next add.
  mintFailures: RejectedDevice[];
  notFound?: string[];
  indexFailures?: RejectedDevice[];
  indexFailureCount?: number;
  // Summary line; present on success/partial results, moves to error.message
  // when nothing was removed.
  message?: string;
}

export interface RemoveDevicesResult {
  status: number;
  message: string | null;
  data: RemoveDevicesResponseData;
}

export async function removeDevices(
  migrationId: string,
  serialNumbers: string[],
): Promise<RemoveDevicesResult> {
  const path = `/v1/migrations/${encodeURIComponent(migrationId)}/devices/remove`;
  try {
    const { status, body: envelope } = await postJsonWithStatus<
      ApiEnvelope<RemoveDevicesResponseData>,
      { devices: string[] }
    >(path, { devices: serialNumbers });
    return {
      status,
      message: envelope.data.message ?? null,
      data: envelope.data,
    };
  } catch (err) {
    // 404 none-removed still has useful per-device detail in the body — the user
    // typed serials that weren't on the migration. Surface those as a result so
    // the dialog can tell them "nothing removed, here's what wasn't found".
    if (
      err instanceof ApiError &&
      err.status === 404 &&
      isApiEnvelope<RemoveDevicesResponseData>(err.body) &&
      err.body.data
    ) {
      return {
        status: err.status,
        message: err.body.error.message ?? null,
        data: err.body.data,
      };
    }
    throw err;
  }
}

// --- POST /v1/migrations/{id}/readiness-override (FD-205) ---------------------
// Grant or clear a readiness override for one or more devices. Bulk-capable by
// design: with no device-side remediate-and-resubmit loop (FD-ADR-027 A2) the
// override is the ONLY way to release a held device, so per-device-only would be
// unusable on a real wave.
//
// `reason` is required on a grant — the override's entire justification is that it
// leaves a decision trail. The operator identity is taken server-side from the
// EasyAuth principal and is never sent from here.
export interface ReadinessOverrideResult {
  migrationId: string;
  override: boolean;
  applied: {
    serialNumber: string;
    override: boolean;
    readinessStatus: string | null;
    overrideBy: string | null;
    overrideUtc: string | null;
    overrideReason: string | null;
  }[];
  notFound: string[];
  failed: { serialNumber: string; reason: string }[];
}

export async function setReadinessOverride(
  migrationId: string,
  serialNumbers: string[],
  override: boolean,
  reason: string,
): Promise<ReadinessOverrideResult> {
  const { body } = await postJsonWithStatus<ApiEnvelope<ReadinessOverrideResult>>(
    `/v1/migrations/${encodeURIComponent(migrationId)}/readiness-override`,
    { serialNumbers, override, reason },
  );
  return body.data;
}

// --- POST /v1/migrations/{id}/delete (FD-477, FD-ADR-044) ---------------------
// Delete a campaign: its devices, identity mappings, events, manifests and
// flight-plan blobs, with the wave revoked first.
//
// `POST .../delete`, not a `DELETE` verb — the route shape `migration-group-delete`
// established. The endpoint performs no destructive work itself: it validates,
// enqueues one message and returns 202 with a `runId`.
//
// `confirm` is the campaign's NAME, and the server compares it. The dialog gates
// its button on the same value, and that is a convenience rather than the control
// — a console-only check is the FD-296 class of gate, one that only runs where
// someone happens to be standing.
export interface DeleteMigrationResult {
  runId: string;
  migrationId: string;
  migrationName: string;
  requestedBy: string;
  requestedUtc: string;
  /** The systems this reaches outside the deployment — rendered verbatim. */
  blastRadius: string[];
}

/** The machine `error.code`s the delete endpoint returns (FD-477 v1.0). */
export type DeleteMigrationErrorCode =
  | 'WAVE_ACTIVE'
  | 'CONFIRMATION_MISMATCH'
  | 'PRINCIPAL_UNRESOLVED'
  | 'MIGRATION_NOT_FOUND';

/** The `data` a `409 WAVE_ACTIVE` carries, so the refusal can name the next step. */
export interface WaveActiveDetail {
  migrationId?: string;
  bprtStatus?: string;
  packagePid?: string;
}

export async function deleteMigration(
  migrationId: string,
  confirm: string,
): Promise<DeleteMigrationResult> {
  const { body } = await postJsonWithStatus<ApiEnvelope<DeleteMigrationResult>>(
    `/v1/migrations/${encodeURIComponent(migrationId)}/delete`,
    { confirm },
  );
  return body.data;
}

// Surfaces the delete endpoint's machine code from a thrown ApiError, preferring
// the envelope's `error.code` (authoritative) over the HTTP status.
//
// The fallback is deliberately NARROW: only 404 maps by status. A 409 is NOT
// assumed to be WAVE_ACTIVE and a 400 is NOT assumed to be CONFIRMATION_MISMATCH,
// because the caller renders WAVE_ACTIVE as reassuring guidance ("complete the
// wave first — nothing was changed"), and showing that for some other 409 would
// tell an operator a comforting thing that is not true.
export function deleteMigrationErrorCode(err: unknown): DeleteMigrationErrorCode | 'UNKNOWN' {
  if (!(err instanceof ApiError)) return 'UNKNOWN';

  if (isApiEnvelope<unknown>(err.body)) {
    const code = err.body.error?.code;
    if (
      code === 'WAVE_ACTIVE' ||
      code === 'CONFIRMATION_MISMATCH' ||
      code === 'PRINCIPAL_UNRESOLVED' ||
      code === 'MIGRATION_NOT_FOUND'
    ) {
      return code;
    }
  }

  if (err.status === 404) return 'MIGRATION_NOT_FOUND';
  return 'UNKNOWN';
}

/** The `data` block on a `409 WAVE_ACTIVE`, or null when the body is unavailable. */
export function waveActiveDetail(err: unknown): WaveActiveDetail | null {
  if (!(err instanceof ApiError)) return null;
  if (!isApiEnvelope<WaveActiveDetail>(err.body)) return null;
  return err.body.data ?? null;
}
